/**
 * RecoveryCodePanel — Ultra Yönetici için tek kullanımlık kurtarma kodu.
 *
 * - `GET /auth/recovery-code/status` — Mevcut kod durumu.
 * - `POST /auth/recovery-code/generate` — Yeni kod üretir. Kodu SADECE 
 *   BİR KEZ döndürür; kullanıcı kaydetmek zorunda.
 * - `DELETE /auth/recovery-code` — Sızıntı şüphesinde kodu iptal eder.
 *
 * Ultra Yönetici hesabı kilitlenirse `/recovery` sayfasından bu kod
 * ile şifreyi sıfırlayıp giriş yapabilir. Backend kod hash'ini bcrypt
 * ile saklar; 5 yanlış deneme sonrası 15 dk kilit uygular.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  KeyRound,
  ShieldAlert,
  Sparkles,
  Copy,
  Check,
  Download,
  Trash2,
  Loader2,
} from "lucide-react";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

export default function RecoveryCodePanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [showCode, setShowCode] = useState(null); // { code, created_at } | null
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/recovery-code/status");
      setStatus(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async () => {
    if (
      status?.has_code &&
      !window.confirm(
        "Yeni kod üretilirse mevcut kod geçersiz olur. Devam etmek istiyor musun?",
      )
    ) {
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/auth/recovery-code/generate");
      setShowCode({ code: data.code, created_at: data.created_at });
      setCopied(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const revoke = async () => {
    if (
      !window.confirm(
        "Kurtarma kodun iptal edilecek. Sızıntı şüphesi yoksa iptal etmene gerek yok. Devam edilsin mi?",
      )
    ) {
      return;
    }
    setRevoking(true);
    try {
      await api.delete("/auth/recovery-code");
      toast.success("Kurtarma kodu iptal edildi");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRevoking(false);
    }
  };

  const copy = async () => {
    if (!showCode?.code) return;
    try {
      await navigator.clipboard.writeText(showCode.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  const download = () => {
    if (!showCode?.code) return;
    const blob = new Blob(
      [
        `Serpix Ultra Yönetici Kurtarma Kodu\n` +
          `Oluşturulma: ${showCode.created_at}\n\n` +
          `KOD: ${showCode.code}\n\n` +
          `Bu kod yalnızca bir kez kullanılabilir. Kayıp/sızıntı halinde \n` +
          `Ayarlar > Güvenlik'ten yenisini oluşturun.\n`,
      ],
      { type: "text/plain;charset=utf-8" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "serpix-recovery-code.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="max-w-2xl rounded-lg border border-zinc-200 bg-white p-6"
      data-testid="recovery-code-panel"
    >
      <div className="mb-4 flex items-center gap-2 border-b border-zinc-100 pb-3">
        <ShieldAlert className="h-4 w-4 text-violet-600" strokeWidth={2} />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-700">
          Ultra Yönetici Kurtarma Kodu
        </h2>
      </div>
      <p className="mb-4 text-xs leading-relaxed text-zinc-600">
        Hesabına giriş yapamazsan (unutulan şifre, e-posta erişim kaybı, …)
        bu kod ile <b>/recovery</b> sayfasından şifreni sıfırlayıp
        girebilirsin. <b>Tek kullanımlıktır</b>; yalnızca oluşturulduğu anda
        gösterilir ve DB&apos;de sadece bcrypt hash&apos;i saklanır.
      </p>

      {loading ? (
        <div className="flex h-16 items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
        </div>
      ) : (
        <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2.5">
          {status?.has_code ? (
            <div className="flex items-center gap-2 text-xs">
              <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              <span className="text-emerald-800">
                <b>Aktif kurtarma kodun var.</b>{" "}
                <span className="text-zinc-600">
                  ({formatDate(status.created_at)})
                </span>
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs">
              <span className="inline-flex h-2 w-2 rounded-full bg-amber-500" />
              <span className="text-amber-800">
                Kurtarma kodun yok. Kilitlenme durumunda giriş yapamayabilirsin.
              </span>
            </div>
          )}
          {status?.used_at && (
            <div className="mt-1 text-[10.5px] text-zinc-500">
              Önceki kod kullanıldı: {formatDate(status.used_at)}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          type="button"
          onClick={generate}
          disabled={generating}
          data-testid="recovery-generate-btn"
          className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:from-violet-700 hover:to-fuchsia-700"
        >
          {generating ? (
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="mr-2 h-3.5 w-3.5" />
          )}
          {status?.has_code ? "Yeni kod üret" : "Kurtarma kodu oluştur"}
        </Button>
        {status?.has_code && (
          <Button
            type="button"
            variant="outline"
            onClick={revoke}
            disabled={revoking}
            data-testid="recovery-revoke-btn"
            className="border-rose-200 text-rose-700 hover:bg-rose-50"
          >
            {revoking ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="mr-2 h-3.5 w-3.5" />
            )}
            İptal et
          </Button>
        )}
      </div>

      <Dialog open={!!showCode} onOpenChange={(o) => !o && setShowCode(null)}>
        <DialogContent
          data-testid="recovery-code-dialog"
          className="max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-zinc-900">
              <KeyRound className="h-4 w-4 text-violet-600" />
              Yeni kurtarma kodun
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900">
              <b>Bu kod yalnızca bir kez gösterilir.</b> Şimdi güvenli bir
              yere kaydet — sonradan yeniden görüntüleyemezsin. Sistem sadece
              bcrypt hash&apos;ini saklar.
            </div>
            <div className="flex items-center justify-between gap-2 rounded-lg border-2 border-dashed border-violet-300 bg-gradient-to-br from-violet-50 to-fuchsia-50 px-4 py-4">
              <div
                data-testid="recovery-code-plaintext"
                className="font-mono text-xl font-bold tracking-widest text-violet-950 select-all"
              >
                {showCode?.code}
              </div>
              <button
                type="button"
                onClick={copy}
                data-testid="recovery-code-copy"
                title="Kopyala"
                className="rounded-md p-2 text-violet-700 hover:bg-white/60"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-emerald-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={download}
              data-testid="recovery-code-download"
              className="w-full"
            >
              <Download className="mr-2 h-3.5 w-3.5" />
              .txt olarak indir
            </Button>
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => setShowCode(null)}
              data-testid="recovery-code-done"
              className="w-full bg-zinc-900 text-white hover:bg-zinc-800"
            >
              Kaydettim, kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
